import { NOTIFICATION_FREQUENCY } from '../constants';
import { IUserCalendar } from './calendar.interfaces';

/** User */
export enum ACCOUNT_STATUS {
  FREE = 'FREE',
  ACTIVE = 'ACTIVE',
  TRIAL = 'TRIAL',
  ACTIVE_BETA = 'ACTIVE_BETA',
}

export interface IGoogleAuthToken {
  access_token: string;
  refresh_token: string;
  scope: string;
  token_type: string;
  id_token: string;
  // optional fields
  expires_in?: number;
  session_state?: {
    extraQueryParams?: {
      authuser?: string;
    };
  };
  first_issued_at?: number;
  expires_at?: number;
  idpId?: string;
  login_hint?: string;
}

interface TimeSlot {
  start: number; // 24hr format
  end: number; // 24hr format
}

type ProductivityStyle =
  | 'balanced'
  | 'earlyBird'
  | 'nightOwl'
  | 'poweredByDeadlines';

export interface IUserSchedulingPreferences {
  productivityStyle: ProductivityStyle;
  preferredWorkingHours: TimeSlot;
  sleepHours: TimeSlot;
}

export interface IPublicProfile {
  isPublic: boolean; // Determines if the profile is publicly visible
  name?: string; // User's full name
  username?: string; // Unique username for the public profile
  usernames?: string[]; // Updated to support multiple usernames
  bio?: string; // User bio, limited to 500 characters
  profilePictureUrl?: string; // URL to user's profile picture
  socialLinks?: {
    twitter?: string;
    linkedin?: string;
    instagram?: string;
    youtube?: string;
    website?: string;
  }; // Object to store user's social media links
}

/* User Interface */
export interface IUser {
  _id?: string; // Optional, as it is generated by MongoDB
  firstName?: string; // User's first name
  lastName?: string; // User's last name
  mobile?: string; // User's mobile number
  email: string; // User's email address
  photoUrl?: string; // URL to the user's profile picture
  bio?: string; // User's bio
  firebaseUid?: string; // Firebase user ID
  auth0Uid?: string[]; // Auth0 user IDs
  chatCompletions?: any[]; // Chat completion data
  provider?: string; // Authentication provider
  totalTaskPoints: number;
  dailyMessageData: {
    count: number; // Number of messages sent today
    monthCount: number; // Number of messages sent this month
    lastMessageDate: Date; // Date of the last message sent
    rateLimitNotified?: boolean; // Whether the user has been notified about rate limits
  };
  settings: {
    isOnboarded: boolean; // Whether the user has completed onboarding
    dailyMessageLimit: number; // Daily message limit
    monthlyMessageLimit: number; // Monthly message limit
    timeZone?: {
      name?: string; // Time zone name
      offset?: number; // Time zone offset
    };
    accountStatus: ACCOUNT_STATUS; // User's account status
    sendReminders: boolean; // Whether to send reminders
    /** @deprecated - Will be removed after migration to new calendar system */
    // googleCalendar?: {
    //   email?: string; // Google Calendar email
    //   googleUserId?: string; // Google User ID
    //   googleAuthResponse?: IGoogleAuthToken;
    // };
    calendars: IUserCalendar[]; // Array of connected calendars
    stripe?: {
      customerId?: string; // Stripe customer ID
      subscriptionId?: string; // Stripe subscription ID
    };
    referralCode?: string; // Referral code
    referrer?: string; // Referrer ID
    chatData?: {
      onboardStatus?: string; // Onboarding status
      welcomeMessageSent?: boolean; // Whether the welcome message has been sent
    };
    verifyMobileData?: {
      isMobileVerified?: boolean; // Whether the mobile number is verified
      verifyMobileCode?: string; // Verification code
      codeExpirationDate?: Date; // Expiration date of the verification code
      unverifiedMobile?: string; // Unverified mobile number
    };
    preferences?: {
      userSchedulingPreferences?: IUserSchedulingPreferences;
      notifications?: {
        channels: {
          email: boolean; // Whether to send email notifications
          sms: boolean; // Whether to send SMS notifications
          push: boolean; // Whether to send push notifications
          reminders: boolean; // Whether to send reminder notifications
        };
        frequency: NOTIFICATION_FREQUENCY; // Notification frequency
        reminderFrequency: NOTIFICATION_FREQUENCY; // Reminder frequency
        subscriptions: {
          messages: boolean; // Subscription to message notifications
          reminders: boolean; // Subscription to reminder notifications
          promotions: boolean; // Subscription to promotion notifications
          systemUpdates: boolean; // Subscription to system update notifications
        };
        lastNotificationSent: {
          messages: Date | null; // Last message notification sent date
          reminders: Date | null; // Last reminder notification sent date
          promotions: Date | null; // Last promotion notification sent date
          systemUpdates: Date | null; // Last system update notification sent date
        };
      };
    };
  };
  publicProfile?: IPublicProfile;
}
